import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import MetricCard from '../components/MetricCard';
import { Sparkles } from 'lucide-react';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import { formatChartDate } from '../lib/formatDate';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import api from '../lib/api';

interface GoogleAdsProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface Segment {
  segment: string;
  spend: number;
  revenue: number;
  clicks: number;
  conversions: number;
  roas: number;
}

interface Campaign extends Segment {
  campaign: string;
}

interface GoogleLive {
  connected: boolean;
  fetched_at?: string;
  account_name: string;
  total_spend: number;
  total_revenue: number;
  total_clicks: number;
  total_conversions: number;
  total_roas: number;
  cost_per_conversion: number;
  daily_trend: { date: string; spend: number; revenue: number }[];
  segment_summary: Segment[];
  campaigns: Campaign[];
}

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const toISO = (d: Date) => format(d, 'yyyy-MM-dd');

const SEGMENT_COLORS: Record<string, string> = {
  PMAX: 'bg-violet-50 text-violet-700 border-violet-100',
  'Brand Search': 'bg-emerald-50 text-emerald-700 border-emerald-100',
  Shopping: 'bg-sky-50 text-sky-700 border-sky-100',
  Generic: 'bg-amber-50 text-amber-700 border-amber-100',
};

export default function GoogleAds({ onHover }: GoogleAdsProps) {
  const [data, setData] = useState<GoogleLive | null>(null);
  const [loading, setLoading] = useState(true);

  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 89); return d; }, []);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, ninetyDaysAgo, today));

  useEffect(() => {
    setLoading(true);
    // Live Windsor.ai query on every request (no Mongo cache) — for 100% data-accuracy alignment
    // with the client's own Google Ads dashboard, same "Choice B" pattern as Meta Ads/ShopifyQL.
    api.get('/revenue-ops/google-ads/live', { params: { start_date: toISO(range.from), end_date: toISO(range.to) } })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [range]);

  const dailyTrend = useMemo(() => (data?.daily_trend || []).map((d) => ({
    date: formatChartDate(d.date),
    spend: d.spend,
    revenue: d.revenue,
  })), [data]);

  const accountName = data?.account_name || 'NOOE India';

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="google-ads-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">
            Search & Shopping Performance
          </span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">
            Google Ads
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">
            Live campaign data queried directly from Windsor.ai for {accountName}, segmented by PMAX / Brand Search / Shopping / Generic.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="google-ads" />
          <div
            data-testid="google-ads-connection-badge"
            className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${
              data?.connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'
            }`}
          >
            <Sparkles className="h-4.5 w-4.5" />
            <span>{data?.connected ? 'Live' : 'Not Connected'}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div data-testid="google-ads-loading" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">
          Querying Windsor.ai live…
        </div>
      ) : !data?.connected ? (
        <div data-testid="google-ads-disconnected" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">
          Google Ads data isn't available yet. Ask your admin to confirm the Windsor.ai connection.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              platform="Google Ads"
              title="Total Spend"
              value={inr(data.total_spend)}
              change="Selected window"
              trend="up"
              insight={{ title: 'Google Ads Spend', metric: 'Total Spend', value: inr(data.total_spend), aiInsight: `${inr(data.total_spend)} spent on ${accountName} in the selected window.`, prediction: 'Trend requires more history to project.', nextSteps: ['Review the segment breakdown below.'] }}
              onHover={onHover}
            />
            <MetricCard
              platform="Google Ads"
              title="Revenue"
              value={inr(data.total_revenue)}
              change="Selected window"
              trend="up"
              insight={{ title: 'Google Ads Revenue', metric: 'Revenue', value: inr(data.total_revenue), aiInsight: `${inr(data.total_revenue)} in conversion value attributed across all live campaigns.`, prediction: 'Trend requires more history to project.', nextSteps: ['Compare revenue by segment below.'] }}
              onHover={onHover}
            />
            <MetricCard
              platform="Google Ads"
              title="ROAS"
              value={`${data.total_roas.toFixed(2)}x`}
              change="Blended"
              trend="neutral"
              insight={{ title: 'Return on Ad Spend', metric: 'ROAS', value: `${data.total_roas.toFixed(2)}x`, aiInsight: `Blended return on ad spend across all segments in the selected window.`, prediction: 'Trend requires more history to project.', nextSteps: ['Check which segment drives the highest ROAS below.'] }}
              onHover={onHover}
            />
            <MetricCard
              platform="Google Ads"
              title="Cost / Conversion"
              value={inr(data.cost_per_conversion)}
              change={`${data.total_conversions.toFixed(1)} conversions`}
              trend="neutral"
              insight={{ title: 'Cost Per Conversion', metric: 'Cost / Conversion', value: inr(data.cost_per_conversion), aiInsight: `Blended cost per conversion across all active campaigns.`, prediction: 'Trend requires more history to project.', nextSteps: ['Compare cost-per-conversion by campaign in the table below.'] }}
              onHover={onHover}
            />
          </div>

          <div data-testid="google-ads-segment-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.segment_summary.map((s) => (
              <div key={s.segment} className="bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm space-y-3" data-testid={`google-ads-segment-${s.segment.toLowerCase().replace(' ', '-')}`}>
                <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${SEGMENT_COLORS[s.segment]}`}>{s.segment}</span>
                <div>
                  <p className="text-[10px] font-mono uppercase text-slate-400">Spend</p>
                  <p className="text-lg font-semibold text-slate-800">{inr(s.spend)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase text-slate-400">Revenue</p>
                  <p className="text-lg font-semibold text-slate-800">{inr(s.revenue)}</p>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                  <span className="text-[10px] font-mono uppercase text-slate-400">ROAS</span>
                  <span className="text-sm font-bold text-emerald-600">{s.roas.toFixed(2)}x</span>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Daily Spend vs. Revenue</h3>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyTrend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} />
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Line type="monotone" dataKey="spend" stroke="#4285F4" strokeWidth={2.5} dot={false} name="Spend (₹)" />
                  <Line type="monotone" dataKey="revenue" stroke="#34A853" strokeWidth={2.5} dot={false} name="Revenue (₹)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="google-ads-campaign-table">
            <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Campaigns by Spend</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-4">Campaign</th>
                    <th className="py-2 pr-4">Segment</th>
                    <th className="py-2 pr-4 text-right">Spend</th>
                    <th className="py-2 pr-4 text-right">Revenue</th>
                    <th className="py-2 text-right">ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {data.campaigns.map((c) => (
                    <tr key={c.campaign} className="border-b border-slate-50 last:border-0">
                      <td className="py-2.5 pr-4 text-slate-700 max-w-md truncate">{c.campaign}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${SEGMENT_COLORS[c.segment]}`}>{c.segment}</span>
                      </td>
                      <td className="py-2.5 pr-4 text-right font-semibold text-slate-800">{inr(c.spend)}</td>
                      <td className="py-2.5 pr-4 text-right text-slate-600">{inr(c.revenue)}</td>
                      <td className="py-2.5 text-right text-slate-600">{c.roas.toFixed(2)}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
